import { useEffect, useMemo, useState } from "react";
import { serviceLabel, type Picklists } from "@pagelink/shared";
import { api, type ScheduleShift } from "./api.ts";
import { Avatar, Icon, Note, divColor } from "./ui.tsx";

type View = "day" | "week" | "month";
const DAY = 86400_000;
const SEP = "\u0001";

/* ---------------------------------------------------------------------------
 * The on-call calendar. Day, week and month, for every division or for one
 * division or service. A rota row is a window; a shift names it (Day, Night).
 * Rows with no shift that run for days, which is how the live rota is held
 * today, show as all-day cover on every day they span. Read-only: the roster
 * is set by the hospital.
 * ------------------------------------------------------------------------ */
export function OnCall({ onPage, onPagePerson }: { onPage: (service: string, sub: string | null) => void; onPagePerson: (id: string) => void }) {
  const [view, setView] = useState<View>("day");
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [filter, setFilter] = useState("");
  const [lists, setLists] = useState<Picklists | null>(null);
  const [rows, setRows] = useState<ScheduleShift[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [person, setPerson] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => { api.picklists().then(setLists).catch(() => {}); }, []);

  const [from, to] = useMemo(() => rangeOf(view, anchor), [view, anchor]);
  useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    api.schedule(from, to).then((r) => live && setRows(r.shifts)).catch(() => live && setError("Could not load the rota."));
    return () => { live = false; };
  }, [from, to]);

  const shortOf = useMemo(() => new Map((lists?.services ?? []).map((s) => [s.name, s.short])), [lists]);
  const label = (s: { service: string; subService: string | null }) => serviceLabel(s.service, s.subService, shortOf.get(s.service));

  // The filter offers every division and service the hospital lists, plus
  // anything on the rota that the list does not have.
  const options = useMemo(() => {
    const listed = lists?.services ?? [];
    const names = new Set(listed.map((s) => s.name));
    const extra = Array.from(new Set((rows ?? []).map((r) => r.service))).filter((n) => !names.has(n)).sort();
    const all = [...listed, ...extra.map((name) => ({ name, grp: "Other", short: null }))];
    return Array.from(new Set(all.map((s) => s.grp))).map((g) => ({
      grp: g,
      items: all.filter((s) => s.grp === g).flatMap((s) => [
        { value: `${s.name}${SEP}`, text: s.name },
        ...(lists?.subServices ?? []).filter((x) => x.division === s.name)
          .map((x) => ({ value: `${s.name}${SEP}${x.name}`, text: ` ${serviceLabel(s.name, x.name, s.short)}` })),
      ]),
    }));
  }, [lists, rows]);

  // A service is covered by its own shifts and by whoever holds its division.
  const [fDiv = "", fSub = ""] = filter.split(SEP);
  const shown = (rows ?? []).filter((r) =>
    (!fDiv || (r.service === fDiv && (!fSub || r.subService === fSub || !r.subService)))
    && (!person || r.clinicianId === person.id));

  const now = Date.now();
  const today = startOfDay(new Date());
  const step = (n: number) => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + n);
    else if (view === "week") d.setDate(d.getDate() + 7 * n);
    else { d.setDate(1); d.setMonth(d.getMonth() + n); }
    setAnchor(startOfDay(d));
  };
  const openDay = (d: Date) => { setAnchor(startOfDay(d)); setView("day"); };

  const title = view === "day"
    ? anchor.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })
    : view === "week"
      ? `${from.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} to ${new Date(+to - 1).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
      : anchor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const isCurrent = view === "day" ? +anchor === +today : now >= +from && now < +to;

  return (
    <>
      <h1 className="screen-title">On call</h1>

      <PersonSearch q={q} setQ={setQ} label={label} onPage={onPagePerson}
        onShifts={(p) => { setPerson(p); setQ(""); setFilter(""); }} />
      {person && (
        <div className="toolrow">
          <span className="pchip">Shifts for {person.name}
            <button className="chip-x" aria-label={`Show everyone, not only ${person.name}`} onClick={() => setPerson(null)}><Icon name="x" size={13} sw={2.4} /></button>
          </span>
        </div>
      )}

      <div className="cal-bar">
        <select aria-label="Division or service" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All divisions</option>
          {options.map((g) => (
            <optgroup key={g.grp} label={g.grp}>
              {g.items.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
            </optgroup>
          ))}
        </select>
        <div className="seg" role="radiogroup" aria-label="Calendar view">
          {(["day", "week", "month"] as View[]).map((v) => (
            <button key={v} role="radio" aria-checked={view === v} onClick={() => setView(v)}>
              {v === "day" ? "Day" : v === "week" ? "Week" : "Month"}
            </button>
          ))}
        </div>
      </div>

      <div className="cal-nav">
        <button className="icon-btn" aria-label="Previous" onClick={() => step(-1)}><Icon name="back" size={17} sw={2} /></button>
        <div className="cal-title">{title}</div>
        {!isCurrent && <button className="btn soft sm" onClick={() => setAnchor(today)}>Today</button>}
        <button className="icon-btn" aria-label="Next" onClick={() => step(1)}>
          <Icon name="back" size={17} sw={2} style={{ transform: "scaleX(-1)" }} />
        </button>
      </div>

      {error && <Note warn>{error}</Note>}
      {!rows && !error && [0, 1, 2].map((i) => (
        <div key={i} aria-hidden="true" style={{ height: 68, marginBottom: 8, borderRadius: 13, background: "var(--soft)", opacity: 1 - i * 0.22 }} />
      ))}

      {rows && view === "day" && (
        <DayList day={anchor} rows={shown} label={label} now={now} onPage={onPage}
          filterLabel={fDiv ? serviceLabel(fDiv, fSub || null, shortOf.get(fDiv)) : null} />
      )}

      {rows && view === "week" && Array.from({ length: 7 }, (_, i) => addDays(from, i)).map((d) => {
        const items = onDay(shown, d);
        return (
          <button key={+d} className={`cal-week-day${+d === +today ? " today" : ""}`} onClick={() => openDay(d)}>
            <div className="cal-week-head">
              <span>{d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>
              {+d === +today && <span className="poschip first">Today</span>}
            </div>
            {items.length === 0
              ? <div className="cal-empty">Nobody on call</div>
              : groupByService(items, label).map(([svc, list]) => (
                  <div key={svc} className="cal-week-svc" style={{ ["--div" as string]: divColor(list[0]!.service) }}>
                    <span className="svc">{svc}</span>
                    <span className="who">
                      {byWindow(list).map(([w, team]) => (
                        <span key={w} className="cal-shiftline">
                          <span className="t">{team[0]!.shift ?? when(team[0]!, d)}</span>
                          <b>{surname(team[0]!.name)}</b>
                          {team.slice(1).map((r) => <span key={r.rowId}> · {surname(r.name)}</span>)}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
          </button>
        );
      })}

      {rows && view === "week" && <div className="fine" style={{ textAlign: "left" }}>First call in bold, then the backup. Tap a day for times.</div>}

      {rows && view === "month" && (
        <div className="cal-month" role="grid" aria-label={title}>
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => <div key={w} className="cal-dow" role="columnheader">{w}</div>)}
          {Array.from({ length: Math.round((+to - +from) / DAY) }, (_, i) => addDays(from, i)).map((d) => {
            const items = onDay(shown, d);
            const first = Array.from(new Set(items.filter((r) => r.position === 1).map((r) => surname(r.name))));
            const out = d.getMonth() !== anchor.getMonth();
            return (
              <button key={+d} role="gridcell" className={`cal-cell${out ? " out" : ""}${+d === +today ? " today" : ""}${fDiv && items.length === 0 ? " gap" : ""}`}
                onClick={() => openDay(d)} aria-label={`${d.toDateString()}, ${items.length} on call`}>
                <span className="n">{d.getDate()}</span>
                {first.slice(0, 2).map((n) => <span key={n} className="nm">{n}</span>)}
                {first.length > 2 && <span className="more">+{first.length - 2}</span>}
                {fDiv && items.length === 0 && <span className="nm">none</span>}
              </button>
            );
          })}
        </div>
      )}
      {rows && view === "month" && <div className="fine" style={{ textAlign: "left", marginTop: 6 }}>First call on each day. Tap a day to see every shift.</div>}
    </>
  );
}

/* Look a person up: who they are, whether they are on call now, and when
 * next. Page them if they are on call; otherwise show their shifts. */
function PersonSearch({ q, setQ, label, onPage, onShifts }: {
  q: string; setQ: (q: string) => void; label: (s: ScheduleShift) => string;
  onPage: (id: string) => void; onShifts: (p: { id: string; name: string }) => void;
}) {
  const [people, setPeople] = useState<{ id: string; name: string; title: string; service: string }[] | null>(null);
  const [ahead, setAhead] = useState<ScheduleShift[]>([]);
  const active = q.trim().length > 0;
  useEffect(() => {
    if (!active || people) return;
    api.clinicians().then((r) => setPeople(r.clinicians)).catch(() => setPeople([]));
    const now = new Date();
    api.schedule(now, addDays(now, 28)).then((r) => setAhead(r.shifts)).catch(() => {});
  }, [active, people]);

  const t = q.trim().toLowerCase();
  const hits = active && people
    ? people.filter((p) => `${p.name} ${p.title} ${p.service}`.toLowerCase().includes(t)).slice(0, 8)
    : [];
  const now = Date.now();
  return (
    <div className="psearch">
      <div className="search">
        <span className="ic"><Icon name="search" size={16} /></span>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person by name, role or service" aria-label="Find a person" />
      </div>
      {active && people && (
        <div className="presults" role="region" aria-live="polite" aria-label="People found">
          {hits.length === 0 && <div className="fine" style={{ textAlign: "left" }}>Nobody matches "{q.trim()}".</div>}
          {hits.map((p) => {
            const mine = ahead.filter((r) => r.clinicianId === p.id).sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
            const nowRow = mine.find((r) => Date.parse(r.startsAt) <= now && Date.parse(r.endsAt) > now);
            const next = mine.find((r) => Date.parse(r.startsAt) > now);
            const at = (iso: string) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
            return (
              <div key={p.id} className="arow">
                <Avatar id={p.id} name={p.name} size={32} fontSize={13} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{p.name}</div>
                  <div className="tt">{p.title} · {p.service}</div>
                  <div className={`tt${nowRow ? " oncall-now" : ""}`}>
                    {nowRow
                      ? `On call now · ${label(nowRow)} · ${nowRow.position === 1 ? "1st call" : "Backup"} until ${new Date(nowRow.endsAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
                      : next ? `Next on call ${at(next.startsAt)} · ${label(next)}` : "No shifts in the next 4 weeks"}
                  </div>
                </div>
                <div className="pacts">
                  {nowRow && <button className="btn soft sm" onClick={() => onPage(p.id)} aria-label={`Page ${p.name}`}><Icon name="pager" size={13} sw={2} />Page</button>}
                  {mine.length > 0 && <button className="linkish" onClick={() => onShifts({ id: p.id, name: p.name })} aria-label={`Show ${p.name}'s shifts`}>Shifts</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DayList({ day, rows, label, now, onPage, filterLabel }: {
  day: Date; rows: ScheduleShift[]; label: (s: ScheduleShift) => string; now: number;
  onPage: (service: string, sub: string | null) => void; filterLabel: string | null;
}) {
  // In the day view a night shift that began yesterday still counts this morning.
  const items = rows.filter((r) => Date.parse(r.startsAt) < +addDays(day, 1) && Date.parse(r.endsAt) > +day);
  if (items.length === 0) {
    return <Note>{filterLabel ? `Nobody is on call for ${filterLabel} on this day. Pages to it go to Locating.` : "Nobody is on call on this day."}</Note>;
  }
  return (
    <>
      {groupByService(items, label).map(([svc, list]) => {
        const { service, subService } = list[0]!;
        return (
          <div key={svc} className="svcgroup" style={{ ["--div" as string]: divColor(service) }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h3 style={{ flex: 1 }}>{svc}</h3>
              <button className="btn soft sm" onClick={() => onPage(service, subService)}><Icon name="pager" size={13} sw={2} />Page</button>
            </div>
            {byWindow(list).map(([w, team]) => {
              const r0 = team[0]!;
              const active = Date.parse(r0.startsAt) <= now && Date.parse(r0.endsAt) > now;
              return (
                <div key={w} className={`cal-shift${active ? " active" : ""}`}>
                  <div className="cal-shift-head">
                    <Icon name="clock" size={12} sw={2.2} />
                    <span>{r0.shift ? <b>{r0.shift}</b> : null}{r0.shift ? " · " : ""}{span(r0, day)}</span>
                    {active && <span className="poschip now">Now</span>}
                  </div>
                  {team.map((r) => (
                    <div key={r.rowId} className="member">
                      <Avatar id={r.clinicianId} name={r.name} size={26} fontSize={11} />
                      <span className="nm">{r.name}</span>
                      <span className="tt">{r.title}</span>
                      <span className={`poschip${r.position === 1 ? " first" : ""}`}>{r.position === 1 ? "1st call" : "Backup"}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
      <Note>A service with nobody listed routes its pages to Locating until the roster is fixed.</Note>
    </>
  );
}

/* ---- dates ---- */

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Weeks start on Monday. A month shows whole weeks, so its grid is square. */
function rangeOf(view: View, anchor: Date): [Date, Date] {
  const monday = (d: Date) => { const x = startOfDay(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
  if (view === "day") return [anchor, addDays(anchor, 1)];
  if (view === "week") { const m = monday(anchor); return [m, addDays(m, 7)]; }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const start = monday(first);
  const end = addDays(monday(last), 7);
  return [start, end];
}
// Calendar days, not 24-hour steps, so a daylight-saving change cannot shift the grid.
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

/**
 * The rows to list under a day in the week and month views: shifts on the day
 * they start, and long rows (more than a day, no shift) on every day they
 * cover, so a night shift is not listed twice.
 */
function onDay(rows: ScheduleShift[], day: Date) {
  const d0 = +day, d1 = +addDays(day, 1);
  return rows.filter((r) => {
    const s = Date.parse(r.startsAt), e = Date.parse(r.endsAt);
    if (s >= d0 && s < d1) return true;
    return !r.shift && e - s > DAY && s < d1 && e > d0;
  });
}

function groupByService(rows: ScheduleShift[], label: (s: ScheduleShift) => string): [string, ScheduleShift[]][] {
  const m = new Map<string, ScheduleShift[]>();
  for (const r of [...rows].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || a.position - b.position)) {
    const k = label(r);
    m.set(k, [...(m.get(k) ?? []), r]);
  }
  return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/** The people sharing one window (a shift), in call order. */
function byWindow(rows: ScheduleShift[]): [string, ScheduleShift[]][] {
  const m = new Map<string, ScheduleShift[]>();
  for (const r of rows) {
    const k = `${r.startsAt}|${r.endsAt}`;
    m.set(k, [...(m.get(k) ?? []), r]);
  }
  return [...m.entries()].map(([k, v]) => [k, v.sort((a, b) => a.position - b.position)]);
}

const hm = (t: number) => new Date(t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

/** "08:00 to 17:00", "17:00 to 08:00 tomorrow", or "All day" for a long row. */
function span(r: ScheduleShift, day: Date) {
  const s = Date.parse(r.startsAt), e = Date.parse(r.endsAt);
  const d0 = +day, d1 = +addDays(day, 1);
  if (s <= d0 && e >= d1) return `All day · until ${new Date(e).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
  const from = s < d0 ? `${hm(s)} yesterday` : hm(s);
  const until = e > d1 ? `${hm(e)} tomorrow` : hm(e);
  return `${from} to ${until}`;
}

/** Compact time for the week list: "08–17", or "all day". */
function when(r: ScheduleShift, day: Date) {
  const s = Date.parse(r.startsAt), e = Date.parse(r.endsAt);
  if (s <= +day && e >= +addDays(day, 1)) return "all day";
  return `${hm(s).slice(0, 2)}–${hm(e).slice(0, 2)}`;
}

const surname = (n: string) => n.replace(/^Dr\.?\s+/, "").split(" ").slice(-1)[0] ?? n;
